import { isDueOn, localDay } from './dates.js';
import { isPausedOn, seedCount } from './engine.js';
import { levelProgress, stageRank, type Rules } from './rules.js';
import {
  SEED_KINDS,
  type Biome, type Daily, type Difficulty, type GameState, type GrowthStage, type Plant, type SeedKind,
} from './types.js';

export type Leaves = 'full' | 'sparse' | 'fillingIn';

export function leavesOf(p: Plant): Leaves {
  if (p.leaves === 'full') return 'full';
  return p.streak > 0 ? 'fillingIn' : 'sparse';
}

const plantFor = (s: GameState, taskId: string) => s.plants.find((p) => p.taskId === taskId && p.kind !== 'weed' && !p.goneAt);

export interface TreeEntry {
  taskId: string;
  plantId: string;
  label: string;
  text: string;
  species: string;
  biome: Biome | null;
  stage: GrowthStage;
  completions: number;
  /** Completions needed for the next stage, or null at mega. */
  nextStageAt: number | null;
  streak: number;
  leaves: Leaves;
  paused: boolean;
}

/** Every tree in every biome, so nobody has to visit a biome to see what needs care. The player's own view. */
export function fieldJournal(s: GameState, rules: Rules): TreeEntry[] {
  return s.tasks
    .filter((t): t is Daily => t.type === 'daily' && !t.archivedAt)
    .flatMap((d) => {
      const p = plantFor(s, d.id);
      if (!p) return [];
      const rank = stageRank(p.stage);
      return [{
        taskId: d.id, plantId: p.id, label: d.label, text: d.text, species: p.species, biome: p.biome,
        stage: p.stage, completions: p.completions,
        nextStageAt: rank < 4 ? rules.stageCounts[p.pace][rank]! : null,
        streak: p.streak, leaves: leavesOf(p), paused: d.paused,
      }];
    });
}

/** What a friend's visit may see: plants and animals, never task text or notes. */
export function gardenView(s: GameState) {
  return {
    starterBiome: s.profile.starterBiome,
    biomes: s.unlocks.map((u) => u.biome),
    plants: s.plants
      .filter((p) => !p.goneAt)
      .map((p) => ({
        id: p.id, kind: p.kind, species: p.species, stage: p.stage, leaves: leavesOf(p), bloomed: p.bloomed,
        biome: p.biome, x: p.x, y: p.y, plot: p.plot,
      })),
    animals: s.inventory
      .filter((i) => i.type === 'animal')
      .map((i) => ({ id: i.id, kind: i.kind, name: i.name, biome: i.biome, homePlantId: i.homePlantId })),
  };
}

export interface Summary {
  day: string;
  paused: boolean;
  level: number;
  xpIntoLevel: number;
  xpForNextLevel: number;
  coins: number;
  seeds: Record<SeedKind, number>;
  biomes: Biome[];
  dailiesDue: { id: string; plantId: string | null; text: string; difficulty: Difficulty; streak: number; completed: boolean; leaves: Leaves }[];
  todosOpen: { id: string; plantId: string | null; text: string; difficulty: Difficulty; dueDate: string | null; drooping: boolean }[];
  habits: { id: string; plantId: string | null; text: string; counterUp: number; counterDown: number; stage: GrowthStage; weeds: number }[];
  wildflowers: { plantId: string; taskId: string | null; text: string; daysLeft: number }[];
}

/** A compact picture of today, for the app's header and for Claude. */
export function summarize(s: GameState, now: Date, rules: Rules): Summary {
  const day = localDay(now, s.profile);
  const lp = levelProgress(s.profile.xp, rules);
  const textOf = (taskId: string | null) => s.tasks.find((t) => t.id === taskId)?.text ?? '';
  const out: Summary = {
    day,
    paused: isPausedOn(s.profile, day),
    level: lp.level,
    xpIntoLevel: lp.into,
    xpForNextLevel: lp.needed,
    coins: s.profile.coins,
    seeds: Object.fromEntries(SEED_KINDS.map((k) => [k, seedCount(s, k)])) as Record<SeedKind, number>,
    biomes: s.unlocks.map((u) => u.biome),
    dailiesDue: [],
    todosOpen: [],
    habits: [],
    wildflowers: s.plants
      .filter((p) => p.kind === 'wildflower' && p.bloomed && !p.goneAt)
      .map((p) => ({ plantId: p.id, taskId: p.taskId, text: textOf(p.taskId), daysLeft: p.bloomDaysLeft ?? 0 })),
  };
  const tasks = s.tasks.filter((t) => !t.archivedAt).sort((a, b) => a.position - b.position);
  for (const t of tasks) {
    const p = plantFor(s, t.id);
    if (t.type === 'daily' && !t.paused && isDueOn(t.repeat, day)) {
      out.dailiesDue.push({
        id: t.id, plantId: p?.id ?? null, text: t.text, difficulty: t.difficulty, streak: p?.streak ?? 0,
        completed: t.completed, leaves: p ? leavesOf(p) : 'full',
      });
    } else if (t.type === 'todo' && !t.completed) {
      out.todosOpen.push({
        id: t.id, plantId: p?.id ?? null, text: t.text, difficulty: t.difficulty, dueDate: t.dueDate,
        drooping: !!t.dueDate && t.dueDate < day,
      });
    } else if (t.type === 'habit') {
      out.habits.push({
        id: t.id, plantId: p?.id ?? null, text: t.text, counterUp: t.counterUp, counterDown: t.counterDown,
        stage: p?.stage ?? 'seed',
        weeds: s.plants.filter((w) => w.kind === 'weed' && w.taskId === t.id && !w.goneAt).length,
      });
    }
  }
  return out;
}
